import { useRef } from "react";
import { MAX_PROOF_BYTES } from "../../utils/paymentProofUrls";

/**
 * Single-file payment proof picker (one image only).
 */
export default function PaymentProofAttachments({
  items = [],
  onChange,
  error = null,
  label = "Payment proof *",
  hint = "Upload one screenshot or photo of the payment (required).",
}) {
  const inputRef = useRef(null);

  const revoke = (preview) => {
    if (preview) URL.revokeObjectURL(preview);
  };

  const setItems = (next) => {
    onChange?.(next);
  };

  const addFiles = (fileList) => {
    const file = Array.from(fileList || []).find(
      (f) => f.type.startsWith("image/") && f.size <= MAX_PROOF_BYTES
    );
    if (!file) return;
    items.forEach((it) => revoke(it.preview));
    setItems([
      {
        id: `${file.name}:${file.size}-${Date.now()}`,
        file,
        preview: URL.createObjectURL(file),
      },
    ]);
    if (inputRef.current) inputRef.current.value = "";
  };

  const removeAt = (id) => {
    const found = items.find((it) => it.id === id);
    if (found) revoke(found.preview);
    setItems(items.filter((it) => it.id !== id));
  };

  const current = items[0] || null;

  return (
    <div>
      <label className="block text-sm font-semibold text-gray-700 mb-2">{label}</label>
      <p className="text-xs text-gray-500 mb-2">{hint}</p>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={(e) => addFiles(e.target.files)}
        className="w-full p-2.5 text-xs md:text-sm border border-gray-300 rounded-lg bg-white text-gray-700 file:mr-3 file:py-1 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-rose-50 file:text-rose-700"
      />
      {current ? (
        <div className="mt-3 relative rounded-lg border border-gray-200 bg-gray-50 overflow-hidden max-w-xs">
          {current.preview ? (
            <img
              src={current.preview}
              alt="Payment proof"
              className="h-28 w-full object-contain bg-white"
            />
          ) : (
            <div className="h-28 flex items-center justify-center text-xs text-gray-500 px-2 text-center">
              {current.file?.name || "File"}
            </div>
          )}
          <button
            type="button"
            onClick={() => removeAt(current.id)}
            className="absolute top-1 right-1 rounded-full bg-black/70 text-white text-[10px] font-semibold px-2 py-0.5 hover:bg-black"
          >
            Remove
          </button>
          <p className="px-2 py-1 text-[10px] text-gray-500 truncate">{current.file?.name}</p>
        </div>
      ) : null}
      {error ? <p className="mt-1 text-sm text-red-600">{error}</p> : null}
    </div>
  );
}

export function PaymentProofGallery({ urls = [], empty = "—" }) {
  const list = Array.isArray(urls) ? urls.filter(Boolean) : [];
  const url = list[0] || "";
  if (!url) {
    return <span className="text-gray-400 text-xs">{empty}</span>;
  }
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1.5"
    >
      <img
        src={url}
        alt="Payment proof"
        className="h-10 w-10 rounded border border-gray-200 object-cover bg-white"
      />
      <span className="text-xs font-semibold text-blue-700 hover:underline">View</span>
    </a>
  );
}
